import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
	FETCH_TRANSLATION_URL,
	TranslationContext,
	TranslationContextState,
	TRANSLATIONS_CACHE_TIMEOUT,
	TRANSLATIONS_PERSIST_KEY,
	translationContextStateDefaults,
} from './const';
import forEach from 'lodash/forEach';
import useFetch from '../../hooks/useFetch';
import { getActiveLanguage, getPersistedTranslations } from './utils';
import { CacheManager } from '../../utils/cacheManager';
import { TranslationContextProps, TranslationFetchResponse, TranslationStateContextProps } from './types';

declare global {
	interface Window {
		__sessionGuid__: string;
	}
}

declare global {
	interface Window {
		__requestVerificationToken__: string;
	}
}

declare global {
	interface Window {
		reactTranslate: (code: number, params?) => string;
	}
}

export const TranslationProvider = ({ children }) => {
	const cacheManagerRef = useRef<CacheManager>(
		new CacheManager(TRANSLATIONS_PERSIST_KEY, TRANSLATIONS_CACHE_TIMEOUT),
	);
	const [translationContext, setTranslationContext] = useState<TranslationStateContextProps>(() => {
		const persistedState = getPersistedTranslations();
		const normalizedPersistedState = persistedState || {};

		return {
			...translationContextStateDefaults,
			...normalizedPersistedState,
		};
	});
	const translate = useCallback(
		(code, params) => {
			let translatedValue = translationContext.translations[code];

			if (typeof translatedValue === 'undefined') {
				return '';
			}

			forEach(params, (value, key) => {
				translatedValue = translatedValue.replace(`{${key}}`, value);
			});

			return translatedValue || '';
		},
		[translationContext.translations],
	);
	const { fetch, response, isPending } = useFetch<TranslationFetchResponse>(FETCH_TRANSLATION_URL);
	const fetchPendingRef = useRef(isPending);
	const shouldFetch = useCallback(() => {
		if (fetchPendingRef.current) {
			return false;
		}

		const persistedContext = getPersistedTranslations();
		const hasInvalidateCacheTime = cacheManagerRef.current.hasInvalidateCacheTime();
		const hasPersistedContext = persistedContext !== null;

		if (hasInvalidateCacheTime) {
			return true;
		}

		if (!hasPersistedContext) {
			return true;
		}

		const cookieActiveLanguageCode = getActiveLanguage();
		//check if persisted language code is up to date
		return persistedContext.activeLanguageCode !== cookieActiveLanguageCode;
	}, []);

	const tryFetch = useCallback(() => {
		if (shouldFetch()) {
			fetch({
				lang: getActiveLanguage(),
			});
		}
	}, [shouldFetch, fetch]);

	useEffect(() => {
		fetchPendingRef.current = isPending;
	}, [isPending]);

	useEffect(() => {
		window.reactTranslate = translate;
	}, [translate]);

	useEffect(() => {
		if (typeof response === 'undefined' || typeof response['data'] === 'undefined') {
			return;
		}

		cacheManagerRef.current.resetStartDate();
		setTranslationContext((prevValue) => ({
			...prevValue,
			...response.data,
			isInitialized: true,
		}));
	}, [response]);

	useEffect(() => {
		if (translationContext.isInitialized) {
			window.localStorage.setItem(TRANSLATIONS_PERSIST_KEY, JSON.stringify(translationContext));
		}
	}, [translationContext]);

	const translationContextAPIValue = useMemo<TranslationContextProps>(
		() => ({
			translate,
			tryFetchTranslations: tryFetch,
		}),
		[translate, tryFetch],
	);

	return (
		<TranslationContextState.Provider value={translationContext}>
			<TranslationContext.Provider value={translationContextAPIValue}>{children}</TranslationContext.Provider>
		</TranslationContextState.Provider>
	);
};
